import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plug, Search, Filter, ArrowUpDown, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useMapWithProvider } from "@/hooks/useMapWithProvider";
import { LocationCard } from "@/components/charging/LocationCard";
import { LocationModal } from "@/components/charging/LocationModal";
import { KavelFaseStrip } from "@/components/charging/KavelFaseStrip";
import { SlapendSchakelaar } from "@/components/charging/SlapendSchakelaar";
import { useKavelSlaap } from "@/hooks/useKavelSlaap";
import { filterKavels, telKavels } from "@/lib/chargingStages";

interface Location {
  id: string;
  name: string;
  stage: string;
  status: string | null;
  address: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  org_id: string | null;
  organizationName?: string | null;
  organization?: { name: string } | null;
  created_at: string;
  /** T2.5 — gevuld = slapend. Bestaat pas na migratie 084. */
  dormant_at?: string | null;
  deleted_at?: string | null;
}

export default function ChargingOperatie() {
  const navigate = useNavigate();
  const location = useLocation();
  const { MapComponent } = useMapWithProvider();
  const [searchTerm, setSearchTerm] = useState("");
  
  // Determine portal context
  const inCRM = location.pathname.startsWith('/crm');
  const basePath = inCRM ? '/crm/charging' : '/charging';
  const portalType = inCRM ? 'crm' : 'charging';
  const [selectedCity, setSelectedCity] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'city'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [toonSlapend, setToonSlapend] = useState(false);
  const { slaapIn, maakWakker } = useKavelSlaap();

  const { data: locations = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['charging-operatie-locations', sortBy, sortOrder],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select(`
          *,
          organizations(id, name)
        `)
        .eq('stage', 'operatie')
        .is('deleted_at', null)
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (error) throw error;
      
      return (data || []).map(loc => ({
        ...loc,
        organizationName: loc.organizations?.name,
        organization: loc.organizations
      })) as Location[];
    },
    retry: 0,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    gcTime: 60000,
  });

  const cities = useMemo(() => {
    const citySet = new Set<string>();
    locations.forEach((l) => {
      if (l.city) citySet.add(l.city);
    });
    return Array.from(citySet).sort();
  }, [locations]);

  // Zoeken/stad/status eerst, slapend daarna — zie ChargingAcquisitie.
  const gezochteLocations = useMemo(() => {
    return locations.filter((location) => {
      const matchesSearch = !searchTerm ||
        location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.organizationName?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCity = selectedCity === "all" || location.city === selectedCity;
      const matchesStatus = selectedStatus === "all" || location.status === selectedStatus;

      return matchesSearch && matchesCity && matchesStatus;
    });
  }, [locations, searchTerm, selectedCity, selectedStatus]);

  const telling = useMemo(
    () => telKavels(gezochteLocations, { toonSlapend }),
    [gezochteLocations, toonSlapend],
  );

  const filteredLocations = useMemo(
    () => filterKavels(gezochteLocations, { toonSlapend }),
    [gezochteLocations, toonSlapend],
  );

  const sitesForMap = useMemo(() => {
    return filteredLocations
      .filter((l) => l.latitude && l.longitude)
      .map((l) => ({
        id: l.id,
        name: l.name,
        address: l.address || '',
        latitude: l.latitude!,
        longitude: l.longitude!,
        site_type: 'Laadlocatie',
        status: l.status || undefined,
      }));
  }, [filteredLocations]);

  const handleLocationClick = (locationId: string) => {
    navigate(`${basePath}/locatie/${locationId}`);
  };

  return (
    <div className="container mx-auto p-4 sm:p-8 w-full max-w-full">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Plug className="h-8 w-8" />
              Operatie
            </h1>
            <p className="text-muted-foreground mt-2">
              Alle actieve laadlocaties
            </p>
          </div>
          <Button onClick={() => setIsLocationModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nieuwe operatie-locatie
          </Button>
        </div>
      </div>

      {/* De vier fasen als hoofdindeling (T2.5). Operatie heeft één stadium,
          dus hier is er niets om verder in te zoomen. */}
      <div className="mb-6">
        <KavelFaseStrip actief="operatie" basePath={basePath} />
      </div>

      {/* Kaart */}
      <Card className="mb-6 overflow-hidden">
        <div className="h-[400px]">
          <MapComponent
            sites={sitesForMap}
            getMarkerColor={() => '#28a745'}
            portalType={portalType}
          />
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Zoek op naam of organisatie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="w-full md:w-48">
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger>
                <SelectValue placeholder="Alle steden" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle steden</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city} value={city}>
                    {city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full md:w-48">
            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Sorteer op..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Naam</SelectItem>
                <SelectItem value="created_at">Aanmaakdatum</SelectItem>
                <SelectItem value="city">Stad</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
          >
            <ArrowUpDown className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-4 md:border-l md:pl-4">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <RadioGroup value={selectedStatus} onValueChange={setSelectedStatus} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="status-all" />
                <Label htmlFor="status-all">Alle</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="actief" id="status-actief" />
                <Label htmlFor="status-actief">Actief</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="in_onderhoud" id="status-onderhoud" />
                <Label htmlFor="status-onderhoud">Onderhoud</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="offline" id="status-offline" />
                <Label htmlFor="status-offline">Offline</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </Card>

      {/* Lijst */}
      <div className="space-y-3">
        <div className="mb-4">
          <SlapendSchakelaar
            telling={telling}
            toonSlapend={toonSlapend}
            onToonSlapend={setToonSlapend}
          />
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Laden...</div>
        ) : isError ? (
          <Card className="p-8 text-center">
            <Plug className="h-12 w-12 mx-auto mb-4 text-destructive opacity-50" />
            <h2 className="text-xl font-semibold mb-2">Kon laadlocaties niet laden</h2>
            <p className="text-muted-foreground mb-4">Er is een fout opgetreden bij het ophalen van de laadlocaties.</p>
            <Button onClick={() => refetch()}>Opnieuw proberen</Button>
          </Card>
        ) : filteredLocations.length === 0 ? (
          <Card className="p-8 text-center">
            <Plug className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Geen laadlocaties gevonden</p>
          </Card>
        ) : (
          filteredLocations.map((location) => (
            <LocationCard
              key={location.id}
              location={location}
              onClick={() => handleLocationClick(location.id)}
              stage="operatie"
              onSlaapIn={slaapIn}
              onMaakWakker={maakWakker}
            />
          ))
        )}
      </div>

      {/* Location Modal */}
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        initialStage="operatie"
      />
    </div>
  );
}
